import { useState, useCallback, useRef } from 'react';

export interface LoadingState<T = any> {
  isLoading: boolean;
  error: Error | null;
  data: T | null;
  isInitialLoad: boolean;
  isRefreshing: boolean;
}

export interface UseLoadingStateOptions {
  initialLoading?: boolean;
  resetErrorOnLoad?: boolean;
  minLoadingTime?: number;
}

export interface UseLoadingStateResult<T = any> extends LoadingState<T> {
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  setData: (data: T | null) => void;
  execute: (promise: Promise<T>) => Promise<T | null>;
  reset: () => void;
  refresh: () => Promise<T | null>;
}

export function useLoadingState<T = any>(
  options: UseLoadingStateOptions = {}
): UseLoadingStateResult<T> {
  const {
    initialLoading = false,
    resetErrorOnLoad = true,
    minLoadingTime = 0
  } = options;

  const [state, setState] = useState<LoadingState<T>>({
    isLoading: initialLoading,
    error: null,
    data: null,
    isInitialLoad: true,
    isRefreshing: false
  });

  const lastPromiseRef = useRef<Promise<T> | null>(null);
  const loadStartTimeRef = useRef<number>(0);

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading: loading,
      error: resetErrorOnLoad && loading ? null : prev.error,
      isRefreshing: !prev.isInitialLoad && loading
    }));
    
    if (loading) {
      loadStartTimeRef.current = Date.now();
    }
  }, [resetErrorOnLoad]);

  const setError = useCallback((error: Error | null) => {
    setState(prev => ({
      ...prev,
      error,
      isLoading: false,
      isRefreshing: false
    }));
  }, []);

  const setData = useCallback((data: T | null) => {
    setState(prev => ({
      ...prev,
      data,
      isLoading: false,
      isInitialLoad: false,
      isRefreshing: false,
      error: null
    }));
  }, []);

  const execute = useCallback(async (promise: Promise<T>): Promise<T | null> => {
    lastPromiseRef.current = promise;
    setLoading(true);

    try {
      const result = await promise;
      
      // Ensure minimum loading time if specified
      if (minLoadingTime > 0) {
        const elapsed = Date.now() - loadStartTimeRef.current;
        const remaining = minLoadingTime - elapsed;
        
        if (remaining > 0) {
          await new Promise(resolve => setTimeout(resolve, remaining));
        }
      }

      // Check if this is still the latest promise
      if (promise === lastPromiseRef.current) {
        setData(result);
        return result;
      }
      
      return null;
    } catch (error) {
      // Check if this is still the latest promise
      if (promise === lastPromiseRef.current) {
        setError(error as Error);
      }
      return null;
    }
  }, [setLoading, setData, setError, minLoadingTime]);

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      data: null,
      isInitialLoad: true,
      isRefreshing: false
    });
    lastPromiseRef.current = null;
  }, []);

  const refresh = useCallback(async (): Promise<T | null> => {
    if (!lastPromiseRef.current) {
      return null;
    }
    
    setState(prev => ({
      ...prev,
      isRefreshing: true,
      error: null
    }));

    return execute(lastPromiseRef.current);
  }, [execute]);

  return {
    ...state,
    setLoading,
    setError,
    setData,
    execute,
    reset,
    refresh
  };
}

// Hook for managing multiple loading states
export function useMultipleLoadingStates<T extends Record<string, any>>(
  keys: (keyof T)[]
): Record<keyof T, UseLoadingStateResult> {
  const states: Partial<Record<keyof T, UseLoadingStateResult>> = {};

  keys.forEach(key => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    states[key] = useLoadingState();
  });

  return states as Record<keyof T, UseLoadingStateResult>;
}

// Hook for sequential loading
export function useSequentialLoading<T = any>() {
  const [queue, setQueue] = useState<Array<() => Promise<T>>>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<T[]>([]);
  const [errors, setErrors] = useState<Error[]>([]);

  const addToQueue = useCallback((operation: () => Promise<T>) => {
    setQueue(prev => [...prev, operation]);
  }, []);

  const processQueue = useCallback(async () => {
    if (isProcessing || queue.length === 0) return;

    setIsProcessing(true);
    const newResults: T[] = [];
    const newErrors: Error[] = [];

    for (const operation of queue) {
      try {
        const result = await operation();
        newResults.push(result);
      } catch (error) {
        newErrors.push(error as Error);
      }
    }

    setResults(prev => [...prev, ...newResults]);
    setErrors(prev => [...prev, ...newErrors]);
    setQueue([]);
    setIsProcessing(false);
  }, [queue, isProcessing]);

  const reset = useCallback(() => {
    setQueue([]);
    setResults([]);
    setErrors([]);
    setIsProcessing(false);
  }, []);

  return {
    addToQueue,
    processQueue,
    isProcessing,
    results,
    errors,
    reset,
    queueLength: queue.length
  };
}